import React from "react";
import { motion, Variants } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import { Box, Container, Typography, Card } from "@mui/material";
import { Check } from "@mui/icons-material";

// UI Components
import CarruselSimple from "../../components/ui/CarruselSimple";
import CajasDeslizantes from "../../components/ui/CajasDeslizantes";
import ContadoresDestacados from "../about/components/ContadoresDestacados";
import BeneficioIcono from "../about/components/BeneficioIcono";
import Partner from "../about/components/Partner";

interface Beneficio {
  texto: string;
  icon: string;
  descripcion: string;
}

interface ValorItem {
  titulo: string;
  desc: string;
}

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.4, delayChildren: 0.2 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  const beneficios = t("home.beneficios", {
    returnObjects: true,
  }) as Beneficio[];

  const valorItems = t("home.por_que_elegirnos.items", {
    returnObjects: true,
  }) as ValorItem[];

  return (
    <Box className="main-content" sx={{ width: "100%", overflow: "hidden" }}>
      {/* 1. CAROUSEL HERO */}
      <CarruselSimple />

      {/* 2. NUESTRAS SOLUCIONES (White / Light Section) */}
      <Box
        component="section"
        id="servicios"
        sx={{
          bgcolor: "#F8F9FA",
          py: { xs: 12, md: 14 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(226, 232, 240, 0.6), transparent)",
          }}
        />

        <Container maxWidth="lg" disableGutters sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 6 } }}>
          <Box sx={{ textAlign: "center", mb: 10, maxWidth: "896px", mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.875rem", sm: "2.25rem", md: "2.5rem" },
                color: "#0056b3",
                lineHeight: 1.25,
                letterSpacing: "-0.02em"
              }}
            >
              {t("home.soluciones_title")}
            </Typography>
          </Box>
          <CajasDeslizantes />
        </Container>
      </Box>

      {/* 3. POR QUÉ ELEGIRNOS COMO SOCIO TECNOLÓGICO (Light Section) */}
      <Box
        component="section"
        id="por-que-elegirnos"
        sx={{
          bgcolor: "#F8F9FA",
          py: { xs: 12, sm: 16 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative line */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(226, 232, 240, 0.6), transparent)",
          }}
        />

        <Container maxWidth="lg" disableGutters sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 6 } }}>
          {/* HEADER */}
          <Box sx={{ mb: { xs: 8, md: 10 }, textAlign: "center", maxWidth: "896px", mx: "auto" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                color: "#06b6d4",
                mb: 2,
                display: "block",
              }}
            >
              {t("home.por_que_elegirnos.subtitulo")}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.875rem", sm: "2.25rem", md: "2.5rem" },
                color: "#0056b3",
                lineHeight: 1.25,
                letterSpacing: "-0.02em"
              }}
            >
              {t("home.por_que_elegirnos.titulo")}
            </Typography>
          </Box>

          {/* 2-Column Grid Layout */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: { xs: 4, md: 5 },
              alignItems: "stretch",
            }}
          >
            {Array.isArray(valorItems) &&
              valorItems.map((val, idx) => (
                <Box
                  key={idx}
                  className="group"
                  sx={{
                    p: { xs: 4, md: 5 },
                    borderRadius: "24px",
                    border: "1px solid",
                    borderColor: "#f1f5f9",
                    bgcolor: "#ffffff",
                    boxShadow: "0 4px 25px rgba(0,0,0,0.015)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      bgcolor: "#EBF5FF",
                      borderColor: "#bfdbfe",
                      boxShadow: "0 15px 35px rgba(0,86,179,0.08)",
                    },
                  }}
                >
                  {/* Glowing background circle */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-64px",
                      right: "-64px",
                      width: "144px",
                      height: "144px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(207, 250, 254, 0.2)",
                      filter: "blur(24px)",
                      transform: "scale(0.5)",
                      opacity: 0,
                      transition: "all 0.5s ease",
                      ".group:hover &": {
                        transform: "scale(1.5)",
                        opacity: 1,
                      }
                    }}
                  />

                  {/* Icon Wrapper */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      position: "relative",
                      zIndex: 10,
                    }}
                  >
                    <Box
                      sx={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ffffff",
                        bgcolor: "#0056b3",
                        boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)",
                        transition: "all 0.3s ease",
                        ".group:hover &": {
                          transform: "scale(1.12)",
                          bgcolor: "#06b6d4",
                        }
                      }}
                    >
                      <Check sx={{ fontSize: "1.5rem" }} />
                    </Box>
                  </Box>

                  {/* Text Content */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, position: "relative", zIndex: 10 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.125rem", sm: "1.25rem" },
                        color: "#0A1128",
                        transition: "color 0.3s ease",
                        ".group:hover &": {
                          color: "#0056b3",
                        }
                      }}
                    >
                      {val.titulo}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#475569",
                        lineHeight: 1.625,
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        fontWeight: 400,
                        transition: "all 0.3s ease",
                        ".group:hover &": {
                          color: "#1e293b",
                          fontWeight: 500,
                        }
                      }}
                    >
                      {val.desc}
                    </Typography>
                  </Box>

                  {/* Right border line */}
                  <Box
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "6px",
                      background: "linear-gradient(to bottom, #06b6d4, #0056b3)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      ".group:hover &": {
                        opacity: 1,
                      }
                    }}
                  />
                </Box>
              ))}
          </Box>
        </Container>
      </Box>

      {/* 4. NUESTRO DELIVERY ACTUAL (Dark Section) */}
      <ContadoresDestacados />

      {/* 5. SECTORES CLAVE (Dark Section with Glow Effect) */}
      <Box
        component="section"
        sx={{
          bgcolor: "#050505",
          py: { xs: 10, md: 14 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Grid */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)",
            backgroundSize: "4rem 4rem",
            maskImage: "radial-gradient(ellipse 80% 50% at 50% 50%, #000 70%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 50% at 50% 50%, #000 70%, transparent 100%)",
            zIndex: 0,
          }}
        />
        {/* Glowing Sphere */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: { xs: "100%", md: "800px" },
            height: "400px",
            backgroundColor: "rgba(0, 229, 255, 0.1)",
            filter: "blur(130px)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" disableGutters sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 6 } }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                color: "#ffffff",
                mb: 2,
                lineHeight: 1.25,
                letterSpacing: "-0.02em"
              }}
            >
              <Trans
                i18nKey="home.industries_title"
                components={[
                  <span key="tech" style={{ color: "#00E5FF" }} />,
                  <span key="sectors" style={{ color: "#00E5FF" }} />,
                ]}
              />
            </Typography>
          </Box>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 3,
                alignItems: "stretch",
                width: "100%",
              }}
            >
              {Array.isArray(beneficios) &&
                beneficios.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <motion.div
                      variants={itemVariants}
                      style={{ display: "flex", width: "100%", height: "100%" }}
                    >
                      <BeneficioIcono
                        texto={item.texto}
                        icono={item.icon}
                        descripcion={item.descripcion}
                      />
                    </motion.div>
                  </Box>
                ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* 6. PARTNERS (White Section) */}
      <Box>
        <Partner />
      </Box>
    </Box>
  );
};

export default HomePage;
